/**
 * Matematica di pan e zoom della mappa, isolata dal DOM per essere testabile.
 *
 * Il "mondo" è il sistema di coordinate del viewBox SVG (0 0 width height).
 * La vista è {k, x, y}: k è il fattore di scala, x/y una traslazione espressa
 * in unità di mondo e applicata prima della scala.
 */

export const MIN_SCALE = 1;

// Oltre 6x la semplificazione della geometria a 110m diventa visibile: le coste
// si vedono spigolose. Lo scopo della mappa è trovare un paese, non ispezionare
// il territorio.
export const MAX_SCALE = 6;

/**
 * Fattore di riempimento dello SVG dentro `box`, in pixel per unità di mondo.
 *
 * È il MASSIMO fra i due rapporti perché lo SVG usa
 * preserveAspectRatio="xMidYMid slice": riempie il riquadro e sborda sull'asse
 * in eccesso, non si adatta al più stretto. Vive qui, esportata, perché la
 * stessa misura serve a tre chiamanti (proiezione del tooltip, mondo visibile,
 * geometria del canvas) e tenerne tre copie significherebbe che una prima o
 * poi diverge.
 */
export function fillUnit(box, world) {
  return Math.max(box.width / world.width, box.height / world.height);
}

/**
 * Porzione di mondo che entra davvero in `box`, in unità di mondo.
 *
 * Su un riquadro proporzionale alla mappa è il mondo intero. Su un riquadro
 * con proporzioni diverse — il caso mobile, 390x340 contro un mondo 1000x436 —
 * lo slice ritaglia l'asse in eccesso, e quello che si vede è una fascia più
 * stretta: è questa la misura che dice quanto mondo resta fuori dallo schermo,
 * cioè quanto pan deve essere permesso.
 */
export function visibleWorld(box, world) {
  const unit = fillUnit(box, world);

  return {
    width: box.width / unit,
    height: box.height / unit,
  };
}

/**
 * Vincola scala e traslazione perché il riquadro non scopra mai i bordi.
 *
 * `visible` è la porzione di mondo inquadrata (vedi visibleWorld) e vale il
 * mondo intero quando il riquadro ha le sue stesse proporzioni. Il valore di
 * default non è una comodità: è il caso desktop, dove non c'è ritaglio.
 *
 * Serve perché il limite di pan è la differenza fra ciò che esiste e ciò che
 * si vede, non fra il mondo e sé stesso. Calcolandolo sul solo mondo — com'era
 * prima — a scala 1 il limite risultava zero SEMPRE, anche quando lo slice
 * stava tagliando via mezza mappa ai lati: sotto il breakpoint tablet la
 * mappa era immobile finché non si premeva "+", e i paesi ritagliati fuori
 * erano irraggiungibili. Con `visible` il limite a scala 1 è la metà che
 * resta fuori, e sull'asse non ritagliato resta correttamente zero.
 */
export function clampView(view, world, visible = world) {
  const k = Math.min(MAX_SCALE, Math.max(MIN_SCALE, view.k));
  // Il max(0, …) non è difensivo per abitudine: un limite negativo
  // invertirebbe Math.min/Math.max qui sotto, e invece di bloccare la vista
  // la farebbe scattare al limite opposto.
  const limitX = Math.max(0, (world.width - visible.width / k) / 2);
  const limitY = Math.max(0, (world.height - visible.height / k) / 2);

  // Il `+ 0` normalizza un -0 che Math.min/Math.max possono propagare quando
  // il limite è 0 (k=1, traslazione negativa in ingresso): -0 è aritmeticamente
  // 0, ma senza normalizzarlo si propagherebbe in una stringa translate()
  // "matematicamente corretta" ma sporca.
  return {
    k,
    x: Math.min(limitX, Math.max(-limitX, view.x)) + 0,
    y: Math.min(limitY, Math.max(-limitY, view.y)) + 0,
  };
}

// Aria attorno al rettangolo inquadrato. Esportata perche' un chiamante che
// vuole solo passare `visible` (quarto argomento) non debba ripetere a mano il
// numero per saltare il terzo.
export const DEFAULT_PADDING = 1.25;

/**
 * Vista che inquadra il rettangolo [x0, y0, x1, y1] espresso in coordinate mondo.
 *
 * `visible` serve solo al clamp finale, non al calcolo della scala: la scala
 * continua a nascere dal rapporto col mondo, cioe' l'inquadratura resta quella
 * gia' validata. Senza passarlo, pero', su un riquadro ritagliato il clamp
 * interno taglierebbe la traslazione a un limite piu' stretto del lecito e il
 * continente scelto resterebbe fuori centro.
 */
export function viewForBounds(bounds, world, padding = DEFAULT_PADDING, visible = world) {
  const x0 = bounds[0];
  const y0 = bounds[1];
  const x1 = bounds[2];
  const y1 = bounds[3];
  const width = Math.max(x1 - x0, 1);
  const height = Math.max(y1 - y0, 1);

  return clampView({
    k: Math.min(world.width / width, world.height / height) / padding,
    x: world.width / 2 - (x0 + x1) / 2,
    y: world.height / 2 - (y0 + y1) / 2,
  }, world, visible);
}

/**
 * Allarga `bounds` fino a contenere anche `points`.
 *
 * I rettangoli dei continenti nell'artefatto della geometria sono calcolati
 * sui poligoni, e i micro-stati un poligono non ce l'hanno: esistono solo come
 * pallino. Inquadrando l'Oceania sui soli poligoni (Australia, Nuova Zelanda,
 * Papua Nuova Guinea) restavano fuori dal riquadro le Isole Marshall e Tuvalu,
 * cioe' paesi che il filtro dice di stare mostrando. Passando qui le ancore di
 * tutti i membri, il rettangolo li comprende.
 *
 * `worldWidth` non e' decorativo: in una proiezione equirettangolare la
 * longitudine si richiude, e Samoa e Tonga (172-175 gradi ovest) cadono al
 * bordo SINISTRO della mappa, a ottocento unita' dal resto dell'Oceania.
 * Includerle vorrebbe dire inquadrare il pianeta intero e perdere il senso del
 * filtro per tutti gli altri: un punto oltre mezzo mondo dal centro del
 * rettangolo sta dall'altra parte dell'antimeridiano e viene lasciato fuori.
 * Resta raggiungibile dalla lista sotto la mappa, che non dipende
 * dall'inquadratura.
 */
export function expandBounds(bounds, points, worldWidth) {
  const centreX = (bounds[0] + bounds[2]) / 2;
  const halfWorld = worldWidth / 2;

  return (points || []).reduce((box, point) => (
    Math.abs(point[0] - centreX) > halfWorld
      ? box
      : [
        Math.min(box[0], point[0]),
        Math.min(box[1], point[1]),
        Math.max(box[2], point[0]),
        Math.max(box[3], point[1]),
      ]
  ), bounds.slice());
}

/**
 * Converte un punto in coordinate mondo nei pixel del riquadro.
 *
 * Lo SVG usa preserveAspectRatio="xMidYMid slice": riempie il riquadro e
 * sborda. La scala effettiva è quindi il MASSIMO fra i due rapporti, non la
 * sola larghezza, e va sommato l'offset del ritaglio. Calcolandola sulla sola
 * larghezza il tooltip finisce nel punto sbagliato su mobile, dove il riquadro
 * ha proporzioni diverse da quelle della mappa.
 */
export function projectToScreen(point, view, world, box) {
  const unit = fillUnit(box, world);
  const offsetX = (box.width - world.width * unit) / 2;
  const offsetY = (box.height - world.height * unit) / 2;

  return {
    unit,
    x: offsetX + (world.width / 2 + (point[0] - world.width / 2 + view.x) * view.k) * unit,
    y: offsetY + (world.height / 2 + (point[1] - world.height / 2 + view.y) * view.k) * unit,
  };
}

// --- zoom da rotella/trackpad ------------------------------------------------

// Sensibilita' dello zoom da rotella/trackpad, in "per pixel" di scroll
// normalizzato (vedi normalizeWheelDeltaY). E' QUESTO il valore da toccare per
// tarare la velocita' complessiva: piu' alto = zoom piu' rapido a parita' di
// scroll. A 0,0008 uno scatto di rotella tipico da 100px da' un fattore di
// circa 1,083 per evento (contro l'1,12 fisso di prima, quindi un po' piu'
// calmo), mentre un pinch da trackpad che emette eventi da ~5px da' circa
// 1,004 per evento invece di 1,12: e' li' che si vedeva la differenza reale,
// perche' il trackpad manda decine di quegli eventi al secondo.
export const WHEEL_ZOOM_SENSITIVITY = 0.0008;

// Pixel-equivalente di una "riga" e di una "pagina" di scroll, per il caso
// (raro ma reale: alcuni mouse e alcune configurazioni di Firefox) in cui
// l'evento wheel riporta deltaY in righe (deltaMode 1) o pagine (deltaMode 2)
// invece che in pixel (deltaMode 0, il caso di trackpad e della maggior parte
// dei mouse moderni). Senza questa conversione un mouse che riporta "3 righe"
// verrebbe letto come deltaY=3 e trattato dalla stessa formula pensata per i
// pixel: con SENSITIVITY 0,0008 darebbe un fattore indistinguibile da 1, cioe'
// uno zoom assurdamente lento rispetto a un trackpad che per lo stesso gesto
// riporta gia' decine o centinaia di pixel. I valori sono approssimazioni
// ragionevoli (altezza di riga tipica, altezza di viewport tipica), non misure
// esatte: qui basta l'ordine di grandezza giusto.
export const WHEEL_LINE_HEIGHT_PX = 16;
export const WHEEL_PAGE_HEIGHT_PX = 800;

/**
 * Normalizza `deltaY` di un evento wheel in pixel, qualunque sia `deltaMode`
 * (0 = pixel, 1 = righe, 2 = pagine). Serve perche' la formula del fattore di
 * zoom e' tarata "per pixel" (vedi WHEEL_ZOOM_SENSITIVITY): senza questa
 * normalizzazione un mouse/browser che riporta righe o pagine invece che
 * pixel produrrebbe un deltaY numericamente piccolo (es. 3 righe) che la
 * stessa formula tradurrebbe in uno zoom quasi impercettibile, mentre un
 * trackpad che per lo stesso gesto fisico riporta gia' pixel a due cifre
 * zoomerebbe normalmente: stesso gesto, risultati incoerenti fra dispositivi.
 *
 * Prende solo `{ deltaY, deltaMode }`, non un vero WheelEvent: cosi' resta
 * testabile con un letterale, senza bisogno del DOM.
 */
export function normalizeWheelDeltaY(event) {
  if (event.deltaMode === 1) {
    return event.deltaY * WHEEL_LINE_HEIGHT_PX;
  }

  if (event.deltaMode === 2) {
    return event.deltaY * WHEEL_PAGE_HEIGHT_PX;
  }

  return event.deltaY;
}

/**
 * Fattore di zoom per un singolo evento wheel, in funzione dello spostamento
 * normalizzato in pixel (vedi normalizeWheelDeltaY).
 *
 * Esponenziale e non lineare: cosi' lo zoom resta moltiplicativo invece che
 * additivo (raddoppiare la scala due volte di seguito deve dare quattro volte
 * la scala di partenza, non tre), ed e' automaticamente simmetrico rispetto al
 * segno di deltaY perche' exp(-x) e' l'esatto reciproco di exp(x): N pixel di
 * scroll in un verso seguiti da N nell'altro riportano alla scala di partenza
 * a meno del solo errore di arrotondamento in virgola mobile.
 *
 * Segno: deltaY negativo (rotella verso l'alto / pinch che allarga) deve
 * ingrandire, quindi il fattore dev'essere > 1 quando deltaPixels < 0 — da cui
 * il meno davanti all'esponente.
 */
export function wheelZoomFactor(deltaPixels) {
  return Math.exp(-deltaPixels * WHEEL_ZOOM_SENSITIVITY);
}
